import { useAnimations, useGLTF } from "@react-three/drei";
import { useEffect } from "react";
import * as THREE from "three";
import { usePlayerAnimationStateManager } from "../../stores/usePlayerAnimationStateManager";

export default function Player(props: CharacterModelProps) {
  // Change the character src to yours
  const model = useGLTF("/models/actors/sol.glb");
  const { actions } = useAnimations(model.animations, model.scene);
  
  /**
   * Character animations setup
   */
  const curAnimation = usePlayerAnimationStateManager(
    (state) => state.curAnimation
  );
  const resetAnimation = usePlayerAnimationStateManager((state) => state.reset);
  const initializeAnimationSet = usePlayerAnimationStateManager(
    (state) => state.initializeAnimationSet
  );

  // Rename your character animations here
  const animationSet = {
    idle: "Idle",
    walk: "Run",
    run: "Sprint",
    jump: "JumpStart",
    jumpIdle: "JumpIdle",
    jumpLand: "JumpLand",
    moveAndJump: "MoveAndJumpStart",
    moveAndJumpIdle: "MoveAndJumpIdle",
    moveAndJumpLand: "MoveAndJumpLand",
    castSpell: "CastSpell",
    fall: "JumpIdle",
  };

  useEffect(() => {
    // Initialize animation set
    initializeAnimationSet(animationSet);

    // Receive and cast Shadows
    model.scene.traverse((child) => {
      if (
        child instanceof THREE.Mesh &&
        child.material instanceof THREE.MeshStandardMaterial
      ) {
        child.receiveShadow = true;
        child.castShadow = true;
      }
    });
  }, []);

  useEffect(() => {
    // Play animation
    const action = actions[curAnimation ? curAnimation : animationSet.idle];

    // For jump and jump land animation, only play once and clamp when finish
    if (
      curAnimation === animationSet.jump ||
      curAnimation === animationSet.jumpLand ||
      curAnimation === animationSet.moveAndJump ||
      curAnimation === animationSet.moveAndJumpLand ||
      curAnimation === animationSet.castSpell
    ) {
      action!
        .reset()
        .fadeIn(0.2)
        .setLoop(THREE.LoopOnce, undefined as unknown as number)
        .play();
      action!.clampWhenFinished = true;
    } else {
      action!.reset().fadeIn(0.2).play();
    }

    // When any action is clamp and finished reset animation
    (action as any)._mixer.addEventListener("finished", () => resetAnimation());

    return () => {
      // Fade out previous action
      action!.fadeOut(0.2);

      // Clean up mixer listener, and empty the _listeners array
      (action as any)._mixer.removeEventListener("finished", () =>
        resetAnimation()
      );
      (action as any)._mixer._listeners = [];
    };
  }, [actions, animationSet.idle, animationSet.jump, animationSet.jumpLand, animationSet.moveAndJump, animationSet.moveAndJumpLand, curAnimation, animationSet.castSpell, resetAnimation]);

  return (
    <primitive
      object={model.scene}
      rotation={[0, 0, 0]}
      scale={0.6}
      position={[0, -1.15, -0.17]}
    />
  );
}

export type CharacterModelProps = JSX.IntrinsicElements["group"];

// Change the character src to yours
useGLTF.preload("/models/actors/sol.glb");
